import ComponentManager from '@/Management/ComponentManager';
import SummaryViewModel from '@/ViewModels/SummaryViewModel';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import DropDownListViewModelT from '@/ViewModels/Controls/DropDownListViewModel';
import ResourceManager from '@/Management/ResourceManager';
import WindowViewModelT from '@/ViewModels/Controls/WindowViewModel';
import LinkViewModelT from '@/ViewModels/Controls/LinkViewModel';
import NavigationService from '@/Management/NavigationService';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import ImageViewModelT from '@/ViewModels/Controls/ImageViewModel';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import LastResponseAcceptor from '@/Utility/LastResponseAcceptor';
import RADApplication from '@/Management/RADApplication';
import NavigationHelper from '@/Management/NavigationHelper';
import Utilities from '@/Utility/Utilities';
import { ISummaryViewContextDTO } from "coreframetypings/dto";
import { SummaryViewDetailViewModel } from '#/CoreFrameInterfaces';

export default class AutoGeneratingSummaryViewModel extends SummaryViewModel {
    protected LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelT>('LinkViewModel');
    protected ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');
    protected DropDownListViewModel = ComponentManager.getViewModel<typeof DropDownListViewModelT>('DropDownListViewModel');
    protected WindowViewModel = ComponentManager.getViewModel<typeof WindowViewModelT>('WindowViewModel');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');

    public scrollTop: number;

    /**
     * Beinhaltet den SummaryViewContext der aktuell dargestellten SummaryView
     */
    public summaryViewContext: KnockoutObservable<ISummaryViewContextDTO>;

    /**
     * Beinhaltet den DetailViewContext der aktuell dargestellten SummaryView
     */
    public detailViewContext: KnockoutObservable<DetailViewContext>;

    /**
     * Beinhaltet den DetailViewContext der SummaryView f�r die Fullscreen-Ansicht
     */
    public fullScreenContext: KnockoutComputed<DetailViewContext>;

    /**
     * Beinhaltet die PdoId f�r die "Aktuelle Ansicht"
     */
    public latestPdoId: KnockoutObservable<string>;

    /**
     * Beinhaltet die aktuell dargestellte PdoId
     */
    public currentPdoId: KnockoutObservable<string>;

    private _initializedPdoId: string;

    /**
     * Beinhaltet den ergonomischen Bezeichner der aktuellen ListView
     */
    public ergName: string;

    /**
     * Definiert den Typen der SummaryView
     */
    public pdTypeName: string;

    /**
     * Gibt an, ob das DropDown f�r die Historisierung angezeigt werden soll
     */
    public showHistory: boolean;

    public openFullScreenSummaryView: boolean;

    /**
     * Die �berschrift der SummaryView (Bsp.: Detailansicht: 0815)
     */
    public label: LabelViewModelT;

    /**
     * Das DropDown zur Auswahl des Historisierungszeitpunkts
     */
    public dropDownList: DropDownListViewModelT;

    /**
     * Das Vergr��erungs-Fenster
     */
    public window: WindowViewModelT;

    /**
     * Der Link zum �ffnen des Vergr��erungs-Fensters
     */
    public link: LinkViewModelT;

    /**
    * Beinhaltet den Text, der dem Benutzer angezeigt wird, wenn es keine Detailansicht gibt.
    */
    public noSummaryViewLabel: LabelViewModelT;

    /**
    * Info-Icon zum Label
    */
    public noSummaryViewInfoImage: ImageViewModelT;

    /**
    * Gibt an, ob Content der SV f�r das Objekt angezeigt werden soll
    */
    public displayContent: KnockoutObservable<boolean>;

    public constructor(params: any) {
        super(params);

        this.displayContent = this.observableOrDefault(true);
        this.scrollTop = this.paramOrDefault(params.scrollTop, 0);
        this.summaryViewContext = this.observableOrDefault(params.summaryViewContext, null);
        this.detailViewContext = this.observableOrDefault(params.detailViewContext, null);
        this.latestPdoId = this.observableOrDefault('-1', null);
        this.currentPdoId = this.observableOrDefault(params.currentPdoId, null);
        this.ergName = this.paramOrDefault(params.ergName, null);
        this.pdTypeName = this.paramOrDefault(params.pdTypeName, null);
        this.showHistory = this.paramOrDefault(params.showHistory, false);
        this.openFullScreenSummaryView = this.paramOrDefault(params.openFullScreenSummaryView, false);
        this.label = this.createLabelViewModel();
        this.dropDownList = this.createDropDownListViewModel();
        this.window = this.createWindowViewModel();
        this.link = this.createLinkViewModel();
        this.noSummaryViewLabel = this.createSummaryViewInactiveLabelViewModel();
        this.noSummaryViewInfoImage = this.createNoSVInfoImageViewModel();

        // Sobald der DVContext geladen wurde, wird die PDOID f�r den "Aktuellen Stand" gesetzt
        this.defineComputed(() => {
            const dvctx = this.detailViewContext();
            if (dvctx && dvctx.isInit() && dvctx.genericObject) {
                const go = dvctx.genericObject;
                const latest = go.currentHistoryPDO_ID && parseInt(go.currentHistoryPDO_ID) > 0 ? go.currentHistoryPDO_ID : go.pdoId;
                let _latest = null;
                ko.ignoreDependencies(() => {
                    _latest = this.latestPdoId();
                });
                if (latest != _latest) {
                    this.latestPdoId(latest);
                }
            }
        });
        
        this.fullScreenContext = this.createFullScreenContextComputed();

        this.defineSubscription(this.currentPdoId, currentPdoId => {
            this.initAsync(currentPdoId);
        });
    }
    
    protected createLabelViewModel(): LabelViewModelT {
        return new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'title',
            prefix: new this.LabelViewModel({
                ergName: 'DETAILVIEW_TITLE',
                postfix: new this.LabelViewModel({
                    caption: ': '
                })
            }),
            caption: this.defineComputed(() => {
                const detailViewContext = this.detailViewContext();

                if (detailViewContext != null && detailViewContext.isInit() && detailViewContext.genericObject != null) {
                    const objectIdentValue = detailViewContext.genericObject.objectIdentValue;
                    return objectIdentValue;
                }

                return '';
            })
        });
    }

    private createDropDownListViewModel(): DropDownListViewModelT {
        return new this.DropDownListViewModel({
            parentLocator: this.locator,
            locator: 'version-selection',
            isAutoWidth: true,
            isToolBarLink: true,
            dataValueField: 'pdoId',
            dataTextField: 'title',
            isVisible: this.showHistory,
            value: this.currentPdoId,
            tooltip: new this.LabelViewModel({
                ergName: 'HISTORY_SELECTION_TOOLTIP'
            }),
            data: this.defineComputed(() => {
                const summaryViewContext = this.summaryViewContext();
                const result = [];


                if (summaryViewContext != null && summaryViewContext.objectHistoryInformations != null) {

                    if (!summaryViewContext.objectHistoryInformations.isCurrentDeleted) {
                        result.push({
                            pdoId: this.latestPdoId(),
                            title: ResourceManager.getInstance().get('SUMMARYVIEW_HISTORY_CURRENT')
                        });
                    }

                    for (let i = 0; i < summaryViewContext.objectHistoryInformations.historyInformation.length; i++) {
                        const historyInformation = summaryViewContext.objectHistoryInformations.historyInformation[i];

                        result.push({
                            pdoId: historyInformation.historizableObjectPDOID,
                            title: historyInformation.historyDate.title,
                            date: historyInformation.historyDate.historizationDate
                        });
                    }
                }


                result.sort((h1, h2) => {
                    if (h1.date == null) return -1;
                    if (h2.date == null) return 1;

                    if (h1.date > h2.date) return -1;
                    if (h1.date < h2.date) return 1;
                    return 0;
                });

                return result;
            })
        });
    }

    protected createWindowViewModel(): WindowViewModelT {
        return new this.WindowViewModel({
            parentLocator: this.locator,
            locator: 'window',
            isModal: true,
            isOpen: false,
            isMaximized: true,
            headline: this.label,
            dropDownList: this.dropDownList,
            allowClose: true,
            technicalName: 'sv-fullscreen'
        });
    }

    protected createLinkViewModel(): LinkViewModelT {
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: 'open-fullscreen',
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'open-fullscreen.image',
                source: GlobalConfiguration.inlineImages.OPEN_FULLSCREEN,
                alternativeText: new this.LabelViewModel({
                    ergName: 'OPEN_FULLSCREEN_TOOLTIP'
                })
            }),
            onClick: (openInNewWindow) => {
                if (openInNewWindow) {
                    const newAddr = NavigationService.getInstance().getAddress();
                    newAddr.setParameter('SelectedPDO', this.latestPdoId());
                    newAddr.setHiddenParameter('OpenFullScreenSummaryView', true);
                    NavigationService.getInstance().navigateToAddress(newAddr,
                        null,
                        null,
                        null,
                        openInNewWindow);
                } else
                    this.window.isOpen(true);
            },
            tooltip: new this.LabelViewModel({
                ergName: 'OPEN_FULLSCREEN_TOOLTIP'
            })
        });
    }

    public async initAsync(pdoId: string) {
        if (this._initializedPdoId === pdoId) {
            return;
        }

        this._initializedPdoId = pdoId;

        const summaryViewRequester = new LastResponseAcceptor(this);

        const summaryViewContext: ISummaryViewContextDTO = await summaryViewRequester.query(RADEnvironmentProxy.getInstance().getSummaryView({
            pdtypename: this.pdTypeName,
            pdoID: pdoId
        }));
        if (!this.validateStatus(summaryViewContext)) {
            if (this.isDisposed) {
                return;
            } else {
                if (!Utilities.isNullOrUndefinedOrBlank(RADApplication.getInstance().currentListViewContext()))
                    RADApplication.getInstance().currentListViewContext().onCorruptElementSelected();
            }
        }

        this.displayContent(summaryViewContext.isSummaryViewActive);

        const viewModel = this.createFakeViewModel();

        const detailViewContext = new DetailViewContext({
            parentLocator: this.locator,
            locator: 'dv',
            detailViewContextDTO: summaryViewContext.detailViewContext,
            viewModel: viewModel,
            pdTypeName: this.pdTypeName,
            isSummaryViewMode: true,
            action: 'VIEW'
        });

        if (this.detailViewContext() && !this.detailViewContext().isDisposed) {
            this.detailViewContext().dispose();
        }

        this.detailViewContext(detailViewContext);
        this.summaryViewContext(summaryViewContext);
    }

    private createFullScreenContextComputed() {
        return this.defineComputed(() => {
            const windowIsOpen = this.window.isOpen();
            const detailViewContext = this.detailViewContext();

            // Wenn das Fenster nicht offen ist, brauchen wir auch keinen DetailViewContext f�r die Fullscreen-Ansicht
            if (!windowIsOpen)
                return null;

            if (!detailViewContext)
                return null;

            if (!detailViewContext.isInit())
                return null;
            
            return new DetailViewContext({
                parentLocator: this.locator,
                locator: 'dv',
                detailViewContextDTO: detailViewContext.detailViewContextDTO,
                viewModel: detailViewContext.viewModel,
                pdTypeName: this.pdTypeName,
                isSummaryViewMode: true,
                action: 'VIEW',
                _detailViewContextDTO: detailViewContext.detailViewContextDTO
            });
        });
    }

    private createSummaryViewInactiveLabelViewModel(): LabelViewModelT {
        return new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'no-summary-view',
            ergName: 'SUMMARYVIEW_NOT_ACTIVE'
        });
    }

    private createNoSVInfoImageViewModel(): ImageViewModelT {
        return new this.ImageViewModel({
            parentLocator: this.locator,
            locator: 'no-summary-view.image',
            source: GlobalConfiguration.inlineImages.INFO_DARK
        })
    }

    /**
     * Erzeugt das FakeViewModel (entspricht einem abgespeckten DetailViewViewModel)
     */
    protected createFakeViewModel(): SummaryViewDetailViewModel {
        const viewModel = {
            createContext: (caller, pdTypeName, pdoId, contextPdoId, contextPdTypeName, contextRelationName, contextParameters, callId, principalFilterMode, filter) => {
                let params = this.createAssociationNavigationParameter(caller, pdTypeName, pdoId, contextPdoId, contextPdTypeName, contextRelationName, contextParameters, callId, principalFilterMode, filter);
                NavigationHelper.navigateAssociation(pdTypeName, pdoId, params);
            },
            confirmCreateContext: (callback) => {
                callback(true);
            },
            viewFactory: {} as any,
            createNestedContext: (nestedContextName, parent) => {
                const nestedDetailViewContextDTO = parent._nestedDetailViewContextDTOs._findByName(nestedContextName);

                if (nestedDetailViewContextDTO == null) {
                    return null;
                }

                return new DetailViewContext({
                    parentLocator: parent.locator,
                    locator: 'dv-nested.[' + nestedContextName + ']',
                    viewModel: viewModel,
                    parent: parent,
                    caller: parent,
                    pdoId: -1,
                    detailViewContextDTO: nestedDetailViewContextDTO,
                    nestedContextName: nestedContextName,
                    isSummaryViewMode: true
                });
            }
        };

        return viewModel;
    }

    /**
     * Erzeugt die optionalen Parameter f�r den Assoziation-Navigator
     * @param caller
     * @param pdTypeName
     * @param pdoId
     * @param contextPdoId
     * @param contextPdTypeName
     * @param contextRelationName
     * @param contextParameters
     * @param callId
     * @param principalFilterMode
     * @param filter
     */
    protected createAssociationNavigationParameter(caller: any, pdTypeName: string, pdoId: string, contextPdoId: string, contextPdTypeName: string, contextRelationName: string, contextParameters: any, callId: string, principalFilterMode: string, filter: string): any {
        return {
            contextSource: 'SV',
            contextTypeName: contextPdTypeName,
            contextObject: contextPdoId,
            contextRelation: contextRelationName,
            contextId: callId,
            principalFilterMode: principalFilterMode,
            filter: filter
        };
    }
    
    public dispose(): void {
        // Der Dvc wird explizit nach den anderen Attributen disposed, 
        // da es in manchen Controls vermehrt zu Fehlern kommen kann, 
        // wenn der DVC vor dem Control/VM disposed wurde.
        const dvcObs = this.detailViewContext;
        this.undisposeProperties.push('detailViewContext');
        this.disposeAsync()
            .then(() => this.disposeObject(dvcObs))
    }
}

ComponentManager.setViewModel('AutoGeneratingSummaryViewModel', AutoGeneratingSummaryViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');